// Every call the admin console makes, as TanStack Query options and mutations, typed by the
// admin API's OpenAPI document. Every one of them is in the admin audit log (D56); reads of one
// organization are in that organization's audit log too.
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { paths } from '../gen/api'
import { API_BASE, ApiError, api, reasonHeader, unwrap } from './client'

type Json<P extends keyof paths, M extends 'get' | 'post' | 'patch'> = paths[P][M] extends {
  responses: { 200: { content: { 'application/json': infer T } } }
}
  ? T
  : never

export type Me = Json<'/me', 'get'>
export type OrgSummary = Json<'/orgs', 'get'>['organizations'][number]
export type OrgDetail = Json<'/orgs/{orgId}', 'get'>
export type Plan = OrgDetail['plan']
export type AppDetail = OrgDetail['projects'][number]['apps'][number]
export type AdminAuditEntry = Json<'/audit', 'get'>['entries'][number]
export type CustomerAuditEntry = Json<'/view/orgs/{orgId}/audit', 'get'>['entries'][number]
export type IssueSummary = Json<'/view/projects/{projectId}/issues', 'get'>['issues'][number]
export type Webhooks = Json<'/webhooks', 'get'>
export type NfseView = Json<'/nfse', 'get'>
export type NfseNote = NfseView['notes'][number]
export type NfseStatus = NfseNote['status']
export type Revenue = Json<'/revenue', 'get'>
export type SystemHealth = Json<'/system', 'get'>
export type Infra = Json<'/infra', 'get'>
export type CostModel = Json<'/cost-model', 'get'>
export type CostItem = CostModel['items'][number]
export type Calibration = Json<'/cost-model/calibration/{month}', 'get'>
export type PlanFit = Json<'/orgs/{orgId}/plan-fit', 'get'>
export type StaffFlag = PlanFit['flags'][number]['flag']
export type QuoteInput =
  paths['/quotes/compute']['post']['requestBody']['content']['application/json']
export type QuoteComputed = Json<'/quotes/compute', 'post'>
export type QuoteResult = QuoteComputed['result']
export type SavedQuote = Json<'/quotes/{id}', 'get'>
export type QuoteSummary = Json<'/quotes', 'get'>['quotes'][number]
export type Abuse = Json<'/abuse', 'get'>
export type Margin = Json<'/margin/{month}', 'get'>
export type Funnel = Json<'/funnel', 'get'>
export type PlatformControls = Json<'/platform/controls', 'get'>
export type ErrorGroups = Json<'/system/errors', 'get'>
export type LogLines = Json<'/system/logs', 'get'>
export type PostHogAlerts = Json<'/system/alerts', 'get'>
export type ProductFlags = Json<'/product-flags', 'get'>
export type LogSearch = NonNullable<paths['/system/logs']['get']['parameters']['query']>

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async () => unwrap(await api.GET('/me')),
  staleTime: 60_000,
  retry: false,
})

export const orgsQuery = (q: string) =>
  queryOptions({
    queryKey: ['orgs', q],
    queryFn: async () => unwrap(await api.GET('/orgs', { params: { query: { q } } })).organizations,
  })

export const orgQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId],
    queryFn: async () => unwrap(await api.GET('/orgs/{orgId}', { params: { path: { orgId } } })),
  })

export const auditQuery = (options: { orgId?: string; limit?: number } = {}) =>
  queryOptions({
    queryKey: ['audit', options.orgId ?? 'all', options.limit ?? 50],
    queryFn: async () =>
      unwrap(
        await api.GET('/audit', {
          params: {
            query: {
              limit: options.limit ?? 50,
              ...(options.orgId ? { org_id: options.orgId } : {}),
            },
          },
        }),
      ).entries,
  })

/** The audit page's size: the next page starts before the last id it holds (APS-412 G1). */
export const AUDIT_PAGE = 100

/** The whole admin audit log, newest first, a page at a time. */
export const auditPagesQuery = () =>
  infiniteQueryOptions({
    queryKey: ['audit', 'all', 'pages'],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/audit', {
          params: { query: { limit: AUDIT_PAGE, ...(pageParam ? { before: pageParam } : {}) } },
        }),
      ).entries,
    getNextPageParam: (last) => (last.length === AUDIT_PAGE ? last.at(-1)?.id : undefined),
  })

// Viewing as the customer: read-only, each request carries staff's reason.

/** The Webhooks page: refreshed every minute while open. */
export const revenueQuery = (month?: string) =>
  queryOptions({
    queryKey: ['revenue', month ?? 'now'],
    queryFn: async () =>
      unwrap(await api.GET('/revenue', { params: { query: month ? { month } : {} } })),
  })

/** Northflank and Cloudflare (APS-336): each source read at most once a minute by the API. */
export const infraQuery = queryOptions({
  queryKey: ['infra'],
  queryFn: async () => unwrap(await api.GET('/infra')),
  refetchInterval: 60_000,
})

export const setInfraBudget = async (input: { budgetUsd: number | null; reason: string }) =>
  unwrap(
    await api.PUT('/infra/budget', {
      body: { infra_budget_usd: input.budgetUsd, reason: input.reason },
    }),
  )

/** D109's cost model: the owner's values over the code's defaults, and what they price. */
export const costModelQuery = queryOptions({
  queryKey: ['cost-model'],
  queryFn: async () => unwrap(await api.GET('/cost-model')),
})

export const calibrationQuery = (month: string) =>
  queryOptions({
    queryKey: ['calibration', month],
    queryFn: async () =>
      unwrap(await api.GET('/cost-model/calibration/{month}', { params: { path: { month } } })),
  })

export type CostItemEdit =
  | {
      value: number
      basis: CostItem['basis']
      source: string
      as_of: string
      note: string
      reason: string
    }
  | { reset: true; reason: string }

export const setCostModelItem = async (input: { key: string; edit: CostItemEdit }) =>
  unwrap(
    await api.PUT('/cost-model/{key}', {
      params: { path: { key: input.key } },
      body: input.edit,
    }),
  )

export const systemQuery = queryOptions({
  queryKey: ['system'],
  queryFn: async () => unwrap(await api.GET('/system')),
  refetchInterval: 60_000,
})

/** Apsio's own funnel and services, from PostHog (step 2.4): aggregates, read once a minute. */
/** The platform's controls (D107): whether new Free organizations are paused. */
export const platformControlsQuery = queryOptions({
  queryKey: ['platform', 'controls'],
  queryFn: async () => unwrap(await api.GET('/platform/controls')),
})

export const setFreeOrgsPaused = async (input: { paused: boolean; reason: string }) =>
  unwrap(await api.POST('/platform/free-orgs', { body: input }))

export const funnelQuery = queryOptions({
  queryKey: ['posthog', 'funnel'],
  queryFn: async () => unwrap(await api.GET('/funnel')),
  refetchInterval: 60_000,
})

export const errorGroupsQuery = queryOptions({
  queryKey: ['posthog', 'errors'],
  queryFn: async () => unwrap(await api.GET('/system/errors')),
  refetchInterval: 60_000,
})

/** PostHog's alerts that are firing, or whose checks fail (D59, APS-469). */
export const posthogAlertsQuery = queryOptions({
  queryKey: ['posthog', 'alerts'],
  queryFn: async () => unwrap(await api.GET('/system/alerts')),
  refetchInterval: 60_000,
})

/** An organization's funnel step and last console activity, from PostHog (D58, APS-469). */
export const orgActivityQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['posthog', 'org', orgId],
    queryFn: async () =>
      unwrap(await api.GET('/orgs/{orgId}/activity', { params: { path: { orgId } } })),
  })

/** Apsio's product flags in PostHog (D58, APS-469). */
export const productFlagsQuery = queryOptions({
  queryKey: ['posthog', 'product-flags'],
  queryFn: async () => unwrap(await api.GET('/product-flags')),
})

/** The product flags that name an organization, or are on for every one. */
export const orgProductFlagsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['posthog', 'product-flags', 'org', orgId],
    queryFn: async () =>
      unwrap(await api.GET('/orgs/{orgId}/product-flags', { params: { path: { orgId } } })),
  })

export const setProductFlagActive = async (input: {
  flagId: number
  active: boolean
  version: number
  reason: string
}) =>
  unwrap(
    await api.PATCH('/product-flags/{flagId}', {
      params: { path: { flagId: input.flagId } },
      body: { active: input.active, version: input.version, reason: input.reason },
    }),
  )

export const setProductFlagTarget = async (input: {
  flagId: number
  orgId: string
  on: boolean
  version: number
  reason: string
}) =>
  unwrap(
    await api.POST('/product-flags/{flagId}/targets', {
      params: { path: { flagId: input.flagId } },
      body: { org_id: input.orgId, on: input.on, version: input.version, reason: input.reason },
    }),
  )

export const logsQuery = (search: LogSearch) =>
  queryOptions({
    queryKey: ['posthog', 'logs', search],
    queryFn: async () => unwrap(await api.GET('/system/logs', { params: { query: search } })),
  })

/** The Abuse page names customers: each read carries staff's reason. */
export const abuseQuery = (reason: string) =>
  queryOptions({
    queryKey: ['abuse', reason],
    queryFn: async () =>
      unwrap(await api.GET('/abuse', { params: { header: reasonHeader(reason) } })),
    staleTime: Number.POSITIVE_INFINITY,
  })

export const marginQuery = (month: string) =>
  queryOptions({
    queryKey: ['margin', month],
    queryFn: async () => unwrap(await api.GET('/margin/{month}', { params: { path: { month } } })),
  })

export const runMargin = async (input: { month: string }) =>
  unwrap(await api.POST('/margin/{month}/run', { params: { path: { month: input.month } } }))

export const setMarginSettings = async (input: {
  domesticTaxRate: number | null
  exportTaxRate: number | null
  accountantMonthlyBrl: number | null
  reason: string
}) =>
  unwrap(
    await api.PUT('/margin/settings', {
      body: {
        domestic_tax_rate: input.domesticTaxRate,
        export_tax_rate: input.exportTaxRate,
        accountant_monthly_brl: input.accountantMonthlyBrl,
        reason: input.reason,
      },
    }),
  )

export const setMonthlyCost = async (input: {
  month: string
  otherBillsUsd: number
  note: string | null
  reason: string
}) =>
  unwrap(
    await api.PUT('/margin/{month}/cost', {
      params: { path: { month: input.month } },
      body: { other_bills_usd: input.otherBillsUsd, note: input.note, reason: input.reason },
    }),
  )

export const webhooksQuery = queryOptions({
  queryKey: ['webhooks'],
  queryFn: async () => unwrap(await api.GET('/webhooks')),
  refetchInterval: 60_000,
})

export const retryWebhook = async (input: { eventId: string; reason: string }) =>
  unwrap(
    await api.POST('/webhooks/events/{eventId}/retry', {
      params: { path: { eventId: input.eventId } },
      body: { reason: input.reason },
    }),
  )

/** A raw-log replay approval (APS-252); its token is in this answer only. */
export const approveReplay = async (input: {
  from: string
  to: string
  org: string | null
  project: string | null
  app: string | null
  dead: boolean
  reason: string
}) => unwrap(await api.POST('/replay-approvals', { body: input }))
export type ReplayApproval = Awaited<ReturnType<typeof approveReplay>>

/** The NFS-e page (D52): every paid invoice's note, open ones first; refreshed every minute. */
export const nfseQuery = queryOptions({
  queryKey: ['nfse'],
  queryFn: async () => unwrap(await api.GET('/nfse', { params: { query: { limit: 500 } } })),
  refetchInterval: 60_000,
})

export const setNfseAutomatic = async (input: { on: boolean; reason: string }) =>
  unwrap(await api.POST('/nfse/automatic', { body: input }))

export const recordNfseNumber = async (input: {
  invoiceId: string
  number: string
  reason: string
  /** Staff checked the DPS in the national portal (required once the DPS was sent). */
  portalChecked?: boolean
}) =>
  unwrap(
    await api.POST('/nfse/invoices/{invoiceId}/number', {
      params: { path: { invoiceId: input.invoiceId } },
      body: {
        number: input.number,
        reason: input.reason,
        ...(input.portalChecked ? { portal_checked: true } : {}),
      },
    }),
  )

/** Downloads a note's XML or PDF: fetched with the admin header, saved through a blob link. */
export async function downloadNfse(
  invoiceId: string,
  kind: 'xml' | 'pdf',
  reason: string,
): Promise<void> {
  const res = await globalThis.fetch(
    `${API_BASE}/nfse/invoices/${encodeURIComponent(invoiceId)}/${kind}`,
    { credentials: 'include', headers: { 'Apsio-Admin': '1', ...reasonHeader(reason) } },
  )
  if (!res.ok) throw new ApiError(res.status, 'download_failed', res.statusText)
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = `nfse-${invoiceId}.${kind}`
  a.click()
  URL.revokeObjectURL(url)
}

export const recordNfseCancellation = async (input: {
  invoiceId: string
  protocol: string
  reason: string
}) =>
  unwrap(
    await api.POST('/nfse/invoices/{invoiceId}/cancellation', {
      params: { path: { invoiceId: input.invoiceId } },
      body: { protocol: input.protocol, reason: input.reason },
    }),
  )

export const cancelNfse = async (input: { invoiceId: string; reason: string }) =>
  unwrap(
    await api.POST('/nfse/invoices/{invoiceId}/cancel', {
      params: { path: { invoiceId: input.invoiceId } },
      body: { reason: input.reason },
    }),
  )

export const sendNfse = async (input: { invoiceId: string; reason: string }) =>
  unwrap(
    await api.POST('/nfse/invoices/{invoiceId}/send', {
      params: { path: { invoiceId: input.invoiceId } },
      body: { reason: input.reason },
    }),
  )

export const viewOrgQuery = (orgId: string, reason: string) =>
  queryOptions({
    queryKey: ['view', orgId, reason, 'org'],
    queryFn: async () =>
      unwrap(
        await api.GET('/view/orgs/{orgId}', {
          params: { path: { orgId }, header: reasonHeader(reason) },
        }),
      ),
    staleTime: Number.POSITIVE_INFINITY,
  })

export const viewAuditQuery = (orgId: string, reason: string) =>
  queryOptions({
    queryKey: ['view', orgId, reason, 'audit'],
    queryFn: async () =>
      unwrap(
        await api.GET('/view/orgs/{orgId}/audit', {
          params: { path: { orgId }, header: reasonHeader(reason), query: { limit: 50 } },
        }),
      ).entries,
    staleTime: Number.POSITIVE_INFINITY,
  })

export const viewIssuesQuery = (projectId: string, reason: string) =>
  queryOptions({
    queryKey: ['view', projectId, reason, 'issues'],
    queryFn: async () =>
      unwrap(
        await api.GET('/view/projects/{projectId}/issues', {
          params: { path: { projectId }, query: { limit: 20 } },
          headers: reasonHeader(reason),
        }),
      ).issues,
    staleTime: Number.POSITIVE_INFINITY,
  })

// Changes. Each refreshes what it changed, and the admin audit log.

// The second factor for a sign-in without a passkey (D56): the authenticator app's code.

export const stepUpQuery = queryOptions({
  queryKey: ['me', 'step-up'],
  queryFn: async () => unwrap(await api.GET('/auth/mfa')),
  retry: false,
})

export type StepUpEnrollment = Json<'/auth/mfa/enroll', 'post'>

export async function enrollTotp(): Promise<StepUpEnrollment> {
  return unwrap(await api.POST('/auth/mfa/enroll'))
}

/** ok, or why not: a wrong code (with the attempts left), locked (until when), or too late. */
export type VerifyOutcome =
  | { ok: true }
  | { ok: false; code: 'invalid_code'; attemptsLeft: number | null }
  | { ok: false; code: 'mfa_locked'; lockedUntil: string | null }
  | { ok: false; code: 'step_up_expired' | 'failed' }

export async function verifyTotp(code: string): Promise<VerifyOutcome> {
  const result = await api.POST('/auth/mfa/verify', { body: { code } })
  if (result.response.ok) return { ok: true }
  const error = (result.error as { error?: Record<string, unknown> } | undefined)?.error ?? {}
  if (error.code === 'invalid_code')
    return {
      ok: false,
      code: 'invalid_code',
      attemptsLeft: typeof error.attempts_left === 'number' ? error.attempts_left : null,
    }
  if (error.code === 'mfa_locked')
    return {
      ok: false,
      code: 'mfa_locked',
      lockedUntil: typeof error.locked_until === 'string' ? error.locked_until : null,
    }
  if (error.code === 'step_up_expired') return { ok: false, code: 'step_up_expired' }
  return { ok: false, code: 'failed' }
}

export async function devSignIn(email: string, name: string, mfa: boolean): Promise<void> {
  unwrap(await api.POST('/auth/dev/session', { body: { email, name, mfa } }))
}

export async function signOut(): Promise<string | null> {
  return unwrap(await api.POST('/auth/sign-out')).sign_out_url
}

export function useAdminMutation<I, O>(run: (input: I) => Promise<O>, refresh: unknown[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await Promise.all([
        ...refresh.map((queryKey) => qc.invalidateQueries({ queryKey })),
        qc.invalidateQueries({ queryKey: ['audit'] }),
      ])
    },
  })
}

export const setLocale = async (locale: 'en' | 'pt-BR') =>
  unwrap(await api.PATCH('/me', { body: { locale } }))

export const extendTrial = async (input: { orgId: string; days: number; reason: string }) =>
  unwrap(
    await api.POST('/orgs/{orgId}/trial', {
      params: { path: { orgId: input.orgId } },
      body: { days: input.days, reason: input.reason },
    }),
  )

export const changePlan = async (input: { orgId: string; plan: Plan; reason: string }) =>
  unwrap(
    await api.POST('/orgs/{orgId}/plan', {
      params: { path: { orgId: input.orgId } },
      body: { plan: input.plan, reason: input.reason },
    }),
  )

/** A past-due organization to reais now, on the switch its owner prepared (D88 addendum). */
export const switchToReaisNow = async (input: { orgId: string; reason: string }) =>
  unwrap(
    await api.POST('/orgs/{orgId}/billing/switch-now', {
      params: { path: { orgId: input.orgId } },
      body: { reason: input.reason },
    }),
  )

// Staff money through Stripe (D56, APS-468): `requestKey` is one per opened form, so a retried
// submit changes nothing.

export const creditBalance = async (input: {
  orgId: string
  amountCents: number
  reason: string
  requestKey: string
}) =>
  unwrap(
    await api.POST('/orgs/{orgId}/billing/credit', {
      params: { path: { orgId: input.orgId } },
      body: {
        amount_cents: input.amountCents,
        reason: input.reason,
        request_key: input.requestKey,
      },
    }),
  )

export const refundInvoice = async (input: {
  orgId: string
  invoiceId: string
  /** Absent: all that is left to refund. */
  amountCents?: number | undefined
  reason: string
  requestKey: string
}) =>
  unwrap(
    await api.POST('/orgs/{orgId}/billing/invoices/{invoiceId}/refund', {
      params: { path: { orgId: input.orgId, invoiceId: input.invoiceId } },
      body: {
        ...(input.amountCents === undefined ? {} : { amount_cents: input.amountCents }),
        reason: input.reason,
        request_key: input.requestKey,
      },
    }),
  )

export const cancelSubscription = async (input: {
  orgId: string
  when: 'period_end' | 'now'
  reason: string
  requestKey: string
}) =>
  unwrap(
    await api.POST('/orgs/{orgId}/billing/cancel', {
      params: { path: { orgId: input.orgId } },
      body: { when: input.when, reason: input.reason, request_key: input.requestKey },
    }),
  )

export const setKind = async (input: {
  orgId: string
  kind: 'customer' | 'sandbox' | 'internal'
  reason: string
}) =>
  unwrap(
    await api.POST('/orgs/{orgId}/kind', {
      params: { path: { orgId: input.orgId } },
      body: { kind: input.kind, reason: input.reason },
    }),
  )

export const setSuspended = async (input: { orgId: string; suspend: boolean; reason: string }) =>
  unwrap(
    input.suspend
      ? await api.POST('/orgs/{orgId}/suspend', {
          params: { path: { orgId: input.orgId } },
          body: { reason: input.reason },
        })
      : await api.POST('/orgs/{orgId}/restore', {
          params: { path: { orgId: input.orgId } },
          body: { reason: input.reason },
        }),
  )

export const setKillSwitch = async (input: {
  appId: string
  on: boolean
  reason: string
  expiresAt: string | null
}) =>
  unwrap(
    await api.POST('/apps/{appId}/kill-switch', {
      params: { path: { appId: input.appId } },
      body: { on: input.on, reason: input.reason, expires_at: input.expiresAt },
    }),
  )

export const setCapture = async (input: {
  appId: string
  rate: number
  reason: string
  expiresAt: string
}) =>
  unwrap(
    await api.POST('/apps/{appId}/capture', {
      params: { path: { appId: input.appId } },
      body: { rate: input.rate, reason: input.reason, expires_at: input.expiresAt },
    }),
  )

export const clearCapture = async (input: { appId: string; reason: string }) =>
  unwrap(
    await api.POST('/apps/{appId}/capture/clear', {
      params: { path: { appId: input.appId } },
      body: { reason: input.reason },
    }),
  )

// Erasures (D83)

export type Erasure = Json<'/erasures', 'get'>['erasures'][number]
export type ErasureStatus = Erasure['status']
export type OrgErasurePreview = Json<'/orgs/{orgId}/erasure', 'get'>
export type UserErasurePreview = Json<'/users/{userId}/erasure', 'get'>

export const erasuresQuery = (status: ErasureStatus | 'open') =>
  queryOptions({
    queryKey: ['erasures', status],
    queryFn: async () =>
      unwrap(await api.GET('/erasures', { params: { query: { status } } })).erasures,
  })

export const orgErasureQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'erasure'],
    queryFn: async () =>
      unwrap(await api.GET('/orgs/{orgId}/erasure', { params: { path: { orgId } } })),
  })

export const userErasureQuery = (userId: string) =>
  queryOptions({
    queryKey: ['user', userId, 'erasure'],
    queryFn: async () =>
      unwrap(await api.GET('/users/{userId}/erasure', { params: { path: { userId } } })),
  })

export const scheduleOrgErasure = async (input: {
  orgId: string
  reason: string
  confirm: string
}) =>
  unwrap(
    await api.POST('/orgs/{orgId}/erasure', {
      params: { path: { orgId: input.orgId } },
      body: { reason: input.reason, confirm: input.confirm },
    }),
  )

export const scheduleUserErasure = async (input: {
  userId: string
  reason: string
  confirm: string
}) =>
  unwrap(
    await api.POST('/users/{userId}/erasure', {
      params: { path: { userId: input.userId } },
      body: { reason: input.reason, confirm: input.confirm },
    }),
  )

/** One of a project's app users, by their user.hash (D92): runs after a day. */
export const scheduleAppUserErasure = async (input: {
  projectId: string
  hash: string
  reason: string
}) =>
  unwrap(
    await api.POST('/projects/{projectId}/app-user-erasure', {
      params: { path: { projectId: input.projectId } },
      body: { hash: input.hash, reason: input.reason },
    }),
  )

export type StopCategory = 'withdrawn' | 'mistake' | 'other'
export const STOP_CATEGORIES: StopCategory[] = ['withdrawn', 'mistake', 'other']

export const cancelErasure = async (input: {
  erasureId: string
  reason: string
  category: StopCategory
}) =>
  unwrap(
    await api.POST('/erasures/{erasureId}/cancel', {
      params: { path: { erasureId: input.erasureId } },
      body: { reason: input.reason, category: input.category },
    }),
  )

export const retryErasure = async (input: { erasureId: string; reason: string }) =>
  unwrap(
    await api.POST('/erasures/{erasureId}/retry', {
      params: { path: { erasureId: input.erasureId } },
      body: { reason: input.reason },
    }),
  )

export const abortErasure = async (input: {
  erasureId: string
  reason: string
  category: StopCategory
}) =>
  unwrap(
    await api.POST('/erasures/{erasureId}/abort', {
      params: { path: { erasureId: input.erasureId } },
      body: { reason: input.reason, category: input.category },
    }),
  )

export const makeOwner = async (input: { orgId: string; userId: string; reason: string }) =>
  unwrap(
    await api.POST('/orgs/{orgId}/members/{userId}/owner', {
      params: { path: { orgId: input.orgId, userId: input.userId } },
      body: { reason: input.reason },
    }),
  )

// D109's plan fit and Enterprise quote (APS-361).
export const planFitQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['plan-fit', orgId],
    queryFn: async () =>
      unwrap(await api.GET('/orgs/{orgId}/plan-fit', { params: { path: { orgId } } })),
  })

/** An organization's service level credit for a month (D76, APS-474). */
export type SlaCredit = Json<'/orgs/{orgId}/sla', 'get'>
export const slaCreditQuery = (orgId: string, month: string) =>
  queryOptions({
    queryKey: ['sla', orgId, month],
    queryFn: async () =>
      unwrap(await api.GET('/orgs/{orgId}/sla', { params: { path: { orgId }, query: { month } } })),
  })

/** Applies the month's SLA credit; the amount is the server's (APS-474). */
export const applySlaCredit = async (input: { orgId: string; month: string; reason: string }) =>
  unwrap(
    await api.POST('/orgs/{orgId}/sla/credit', {
      params: { path: { orgId: input.orgId } },
      body: { month: input.month, reason: input.reason },
    }),
  )

export const planFitsQuery = queryOptions({
  queryKey: ['plan-fit'],
  queryFn: async () => unwrap(await api.GET('/plan-fit')).organizations,
})

export const setStaffFlag = async (input: {
  orgId: string
  flag: StaffFlag
  on: boolean
  note: string
  reason: string
}) =>
  unwrap(
    await api.PUT('/orgs/{orgId}/flags/{flag}', {
      params: { path: { orgId: input.orgId, flag: input.flag } },
      body: { on: input.on, note: input.note, reason: input.reason },
    }),
  )

export const computeQuote = async (input: QuoteInput) =>
  unwrap(await api.POST('/quotes/compute', { body: input }))

export const saveQuote = async (input: {
  orgId: string | null
  name: string
  note: string
  inputs: QuoteInput
  reason: string
}) =>
  unwrap(
    await api.POST('/quotes', {
      body: {
        org_id: input.orgId,
        name: input.name,
        note: input.note,
        inputs: input.inputs,
        reason: input.reason,
      },
    }),
  )

export const quotesQuery = (orgId?: string) =>
  queryOptions({
    queryKey: ['quotes', orgId ?? null],
    queryFn: async () =>
      unwrap(await api.GET('/quotes', { params: { query: orgId ? { org_id: orgId } : {} } }))
        .quotes,
  })

export const quoteQuery = (id: string) =>
  queryOptions({
    queryKey: ['quote', id],
    queryFn: async () => unwrap(await api.GET('/quotes/{id}', { params: { path: { id } } })),
  })
